import { HttpResponse } from './api-types'

// Object.fromEntries is to add headers when poly-filling

async function parseResponse<T>(response: Response): Promise<HttpResponse<T>> {
  switch (response.headers.get('Content-Type')) {
    case 'application/pdf':
    case 'application/pdf;charset=UTF-8':
    case 'text/csv;charset=UTF-8':
    case 'text/csv;charset=windows-1252':
      return {
        contentType: 'blob',
        blob: await response.blob(),
        fileName: findFileName(response),
      }
    case 'text/html':
    case 'text/html; charset=utf-8':
      return {
        contentType: 'text/html',
        text: (await response.text()) as unknown as T,
      }
    case null:
    case undefined:
      return { contentType: 'no-content', nothing: (await response.body) as unknown as T }
    default:
      return { contentType: 'json', json: await response.json() }
  }
}

export async function http<T>(path: string, request: RequestInit): Promise<HttpResponse<T>> {
  const response: Response = await fetch(path, request)
  if (!response.ok) {
    let errorText
    try {
      const json = await response.json()
      errorText = json.errorCode || json.message
    } catch (_ex) {
      errorText = response.statusText
    }
    throw new Error(errorText)
  }

  try {
    return await parseResponse(response)
  } catch (_ex) {
    return { contentType: 'unknown' }
  }
}

export async function get<T>(
  path: string,
  token: string | undefined = undefined,
  args: RequestInit = { method: 'GET' }
): Promise<HttpResponse<T>> {
  const headers = setHeaders(token, undefined)
  return http<T>(path, { ...args, headers: Object.fromEntries(headers) })
}

export async function post<T>(
  path: string,
  token: string | undefined,
  body: Record<string, unknown>,
  includeHeaders = true,
  args: RequestInit = { method: 'POST', body: JSON.stringify(body) }
): Promise<HttpResponse<T>> {
  const headers = includeHeaders ? setHeaders(token, 'application/json') : []
  return http<T>(path, { ...args, headers: Object.fromEntries(headers) })
}

export async function put<T>(
  path: string,
  token: string | undefined,
  body: Record<string, unknown>,
  args: RequestInit = { method: 'PUT', body: JSON.stringify(body) }
): Promise<HttpResponse<T>> {
  const headers = setHeaders(token, 'application/json')
  return http<T>(path, { ...args, headers: Object.fromEntries(headers) })
}

export async function patch<T>(
  path: string,
  token: string | undefined,
  body: Record<string, unknown>,
  args: RequestInit = { method: 'PATCH', body: JSON.stringify(body) }
): Promise<HttpResponse<T>> {
  const headers = setHeaders(token, 'application/merge-patch+json')
  return http<T>(path, { ...args, headers: Object.fromEntries(headers) })
}

export async function fetchDelete<T>(
  path: string,
  token: string | undefined,
  args: RequestInit = { method: 'DELETE' }
): Promise<HttpResponse<T>> {
  const headers = setHeaders(token, 'application/json')
  return http<T>(path, { ...args, headers: Object.fromEntries(headers) })
}

export async function download(
  path: string,
  token: string | undefined = undefined,
  args: RequestInit = { method: 'GET' }
): Promise<HttpResponse<undefined>> {
  const headers = setHeaders(token)
  return http<undefined>(path, { ...args, headers: Object.fromEntries(headers) })
}

function setHeaders(token?: string, contentType?: string) {
  const headers = new Headers()
  headers.append('ET-Client-Name', 'entur-fordelsreise')
  if (token) {
    headers.append('Authorization', `Bearer ${token}`)
  }
  if (contentType) {
    headers.append('Content-type', contentType)
  }
  return headers
}

function findFileName(response: Response): string | undefined {
  const fileName = response.headers?.get('Content-Disposition')?.split('filename=')
  return fileName ? fileName[1].replace(/"/g, '') : undefined
}
